// CODE COPIED FROM
// https://next.material-ui.com/components/autocomplete/#country-select
import {useEffect, useState} from 'react';
import {
  Box,
  Autocomplete,
  AutocompleteChangeReason,
  AutocompleteChangeDetails,
  Typography,
  IconButton,
} from '@mui/material';
import {ArrowDropDown} from '@mui/icons-material';
import {useTranslation} from 'react-i18next';
import {translations} from '../../i18n/translations';
import {AppLanguageTag} from '../../i18n/types';
import {CountryType} from '../../interfaces/country-type';
import {countries} from '../../i18n/countries';
import FlatInput from '../flat-input/flat-input';
import {getCountryFlagByCDN} from '../../utils/functions';

function getCountryByCountryCode(countryCode: string | undefined): CountryType | undefined {
  // Search
  for (let country of countries) {
    // if it matches
    if (country.code.toString() === countryCode?.trim()) {
      // Set result
      return country;
    }
  }
}

/**
 * Country select component used in the phone number input.
 */
export default function CountrySelect(props: {
  value?: {
    code: string;
    phone: string;
  };
  onChange?: (value: {code: string; phone: string}) => void;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  handleBlur?: () => void;
}) {
  // Calculate Default value
  let defaultValue: CountryType = {
    code: 'PT',
    label: {
      [AppLanguageTag.esES]: 'Portugal',
      [AppLanguageTag.glES]: 'Portugal',
      [AppLanguageTag.ptPT]: 'Portugal',
      [AppLanguageTag.enUS]: 'Portugal',
      [AppLanguageTag.ptBR]: 'Portugal',
    },
    phone: '351',
  };
  if (props.value) {
    // Search for it
    const code: string = props.value.code.trim();
    for (let country of countries) {
      if (country.code.toString() === code) {
        defaultValue = country;
        break;
      }
    }
  }

  // Hold State
  const [value, setValue] = useState<CountryType>(defaultValue);

  // Translations
  const {t, i18n} = useTranslation();

  // On mount
  useEffect(() => {
    // Update value
    const newValue: CountryType | undefined = getCountryByCountryCode(props.value?.code);
    if (newValue) {
      setValue(newValue);
    }
  }, [props.value]);

  return (
    <Autocomplete
      fullWidth
      autoHighlight
      disableClearable
      noOptionsText={t(translations.country_select.no_options)}
      disabled={props.disabled}
      options={countries}
      defaultValue={defaultValue}
      isOptionEqualToValue={(option: CountryType, value: CountryType) => {
        return option.code === value.code;
      }}
      filterOptions={(displayOptions, state) => {
        return displayOptions.filter((option) =>
          option.label[i18n.language as AppLanguageTag]
            .toLowerCase()
            .trim()
            .includes(state.inputValue.toLowerCase().trim())
        );
      }}
      getOptionLabel={(option) =>
        `+${option.phone} ${option.label[i18n.language as AppLanguageTag]} (${option.code})`
      }
      renderOption={(props, option) => (
        <Box component="li" sx={{fontSize: 15, '& > span': {mr: '10px', fontSize: 18}}} {...props}>
          <span>
            <img
              loading="lazy"
              width="20"
              {...getCountryFlagByCDN(option.code)}
              alt={`Country flag for ${option.code}`}
            />
          </span>
          <Typography variant="body1">
            {option.label[i18n.language as AppLanguageTag]} ({option.code}) +{option.phone}
          </Typography>
        </Box>
      )}
      ListboxProps={{
        sx: {
          width: '100%',
        },
      }}
      componentsProps={{
        paper: {
          sx: {
            transform: 'translate(-10px, 10px)',
            width: 'max-content',
          },
        },
      }}
      renderInput={(params) => {
        return (
          <Box
            sx={{
              width: '66px',
              height: '40px',
              display: 'grid',
              placeItems: 'center',
              borderRadius: '5px 5px 0 0',
              '&:hover, &:active': {
                filter: props.disabled ? 'none' : 'brightness(95%)',
              },
              background: props.disabled ? 'var(--dcm-white-darker)' : 'var(--dcm-blue-hover)',
            }}
          >
            <FlatInput
              {...params}
              error={props.error}
              InputLabelProps={undefined}
              inputProps={{
                ...params.inputProps,
              }}
              sx={{
                opacity: 0,
              }}
              disabled={props.disabled}
              fullWidth
            />
            <Box
              {...params.inputProps}
              ref={params.InputProps.ref}
              // @ts-ignore
              onClick={params.InputProps.onClick}
              // @ts-ignore
              onBlur={params.InputProps.onBlur}
              sx={{
                position: 'absolute',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '5px',
                pointerEvents: props.disabled ? 'none' : 'all',
              }}
            >
              {props.value?.code ? (
                <img
                  loading="lazy"
                  width="20"
                  {...getCountryFlagByCDN(props.value.code)}
                  alt={`Selected country flag for ${props.value.code}`}
                />
              ) : undefined}
              <IconButton size="small" sx={{height: '25px', width: '25px'}}>
                <ArrowDropDown />
              </IconButton>
            </Box>
          </Box>
        );
      }}
      value={value}
      onChange={(
        _event: React.SyntheticEvent,
        value: CountryType | null,
        _reason: AutocompleteChangeReason,
        _details?: AutocompleteChangeDetails<CountryType>
      ) => {
        // Callback
        if (value && props.onChange) {
          props.onChange({
            code: value.code,
            phone: `+${value.phone}`,
          });
        }

        // Update Value
        if (value) {
          setValue(value);
        }
      }}
      onBlur={props.handleBlur}
    />
  );
}
